// @ts-nocheck

import _, { debounce } from 'lodash';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { ViewportWidthRef, ViewportHeightRef, ViewportRatioMin } from '../../constants/ui/viewport';
import { ViewportContext } from '../../contexts/ViewportContext';
import { getPortalRootDOMElement, getAppDOMElement } from '../../DOMElements';
import { isIOS, isIPhoneX, isIPhoneXSMax, isEmbedded } from '../../lib/useragent';
import { ViewportOrientations } from '../../types/viewport';
import { triggerScroll } from '../../utils';
import { getAppBarHeight, getFontSize, getSafeAreaInset } from '../../utils/layout';

const SafeAreaInset = styled.div`
  position: fixed;
  top: -99999px;
  left: -99999px;
  padding-top: constant(safe-area-inset-top);
  padding-top: env(safe-area-inset-top);
  padding-left: constant(safe-area-inset-left);
  padding-left: env(safe-area-inset-left);
  padding-right: constant(safe-area-inset-right);
  padding-right: env(safe-area-inset-right);
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
`;

type ViewportPropsType = {
  children: React$Node;
};

const Viewport = ({ children }: ViewportPropsType) => {
  const [state, setState] = React.useState({
    fontSize: 14,
    navbarHeight: 0,
    tabbarHeight: 0,
    safeAreaInset: { bottom: 0 },
    width: 0,
    height: 0,
  });
  const domNodeRef = React.useRef<HTMLElement | null>(null);

  const { fontSize, safeAreaInset } = state || {};
  const safeAreaInsetRef = useRef();

  useLayoutEffect(() => {
    safeAreaInsetRef.current = safeAreaInset;
  }, [safeAreaInset]);

  const mutate = useCallback((mutation) => {
    if (mutation && typeof mutation === 'object' && Object.keys(mutation).length) {
      setState((prev) => ({ ...prev, ...mutation }));
    }
  }, []);

  useEffect(() => {
    let didCancel = false;

    const update = debounce(() => {
      const w = Math.max(document.documentElement?.clientWidth ?? 0, window.innerWidth || window.outerWidth || 0);
      const h = Math.max(document.documentElement?.clientHeight ?? 0, window.innerHeight || window.outerHeight || 0);

      let width = w;
      let height = h;

      const orientation = width >= height ? ViewportOrientations.LANDSCAPE : ViewportOrientations.PORTRAIT;

      if ((isIPhoneX || isIPhoneXSMax) && isEmbedded) {
        if (orientation === ViewportOrientations.PORTRAIT) {
          width = isIPhoneXSMax ? 414 : 375;
          height = isIPhoneXSMax ? 896 : 812;
        } else {
          height = isIPhoneXSMax ? 414 : 375;
          width = isIPhoneXSMax ? 896 : 812;
        }
      }

      let ratioX = width / ViewportWidthRef;
      let ratioY = height / ViewportHeightRef;

      if (orientation === ViewportOrientations.LANDSCAPE) {
        ratioX = width / ViewportHeightRef;
        ratioY = height / ViewportWidthRef;
      }

      const ratio = Math.max(Math.min(ratioX, ratioY), ViewportRatioMin) * 0.65;

      if (!didCancel) {
        setState((prevState) => {
          const safeAreaInset = domNodeRef && domNodeRef.current ? getSafeAreaInset(domNodeRef.current) : {};

          let shouldUpdate = false;

          if (prevState) {
            if (
              prevState.width !== width ||
              prevState.height !== height ||
              !_.isEqual(prevState.safeAreaInset, safeAreaInset)
            ) {
              shouldUpdate = true;
            }
          } else {
            shouldUpdate = true;
          }

          if (shouldUpdate) {
            const fontSize = getFontSize(ratio);
            const { navbarHeight, tabbarHeight } = getAppBarHeight(fontSize, safeAreaInset);

            return {
              orientation,
              width,
              height,
              ratio,
              fontSize,
              safeAreaInset,
              navbarHeight,
              tabbarHeight,
              domNodeRef,
            };
          }
          return prevState;
        });
      }

      // trigger scroll to fix tabbar not visible on ios browser
      // https://redmine.winamax.fr/issues/116917
      if (!isEmbedded && isIOS) {
        triggerScroll();
      }
    }, 100);

    window.addEventListener('resize', update);
    update();

    const intervalId = setInterval(() => {
      const { top, right, bottom, left } = safeAreaInsetRef.current ?? {};

      if (!top && !right && !bottom && !left) {
        update();
      } else {
        clearInterval(intervalId);
      }
    }, 1000);

    const timeoutId = setTimeout(() => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    }, 10000);

    return () => {
      didCancel = true;
      window.removeEventListener('resize', update);

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (fontSize) {
      const portalRootDOMElement = getPortalRootDOMElement();
      portalRootDOMElement.style.fontSize = `${fontSize}px`;
      portalRootDOMElement.style.lineHeight = 'normal';
      const appDOMElem = getAppDOMElement();
      appDOMElem.style.fontSize = `${fontSize}px`;
      appDOMElem.style.lineHeight = 'normal';
    }
  }, [fontSize]);

  const enhancedState = useMemo(() => ({ ...state, mutate }), [state, mutate]);

  return (
    <>
      {state ? <ViewportContext.Provider value={enhancedState}>{children}</ViewportContext.Provider> : null}
      <SafeAreaInset ref={domNodeRef} />
    </>
  );
};

export default Viewport;
